import DeleteModal from '@/components/Modals/DeleteModal';
import { useRefetchPresentersList } from '@/hooks';
import { useDeleteImage } from '@/hooks/useDeleteImage';
import { useDeleteAvatar, useDeletePresenter, useDeleteTrain } from '@/pages/avatar';
import { ImageType, isDvpPresenter, isDvpTrain, isExpressPresenter, Presenter } from '@d-id/types';
import { atom, useRecoilState } from 'recoil';

export const avatarToDeleteAtom = atom<Presenter | null>({
    key: 'avatar-to-delete',
    default: null,
});

export const DeleteAvatarModal = () => {
    const [avatarToDelete, setAvatarToDelete] = useRecoilState(avatarToDeleteAtom);
    const { mutateAsync: deletePresenterImage, isLoading: isDeletingPresenterImage } = useDeleteImage(ImageType.Upload);
    const { mutateAsync: deleteCustomDream, isLoading: isDeletingCustomDream } = useDeleteImage(ImageType.Dreams);
    const { mutateAsync: deleteAvatar, isLoading: isDeletingAvatar } = useDeleteAvatar();
    const { mutateAsync: deletePresenter, isLoading: isDeletingPresenter } = useDeletePresenter();
    const { mutateAsync: deleteTrain, isLoading: isDeletingTrain } = useDeleteTrain();
    const { refetchPresentersList } = useRefetchPresentersList();

    const handleDelete = async () => {
        if (!avatarToDelete?.id) {
            return;
        }

        if (avatarToDelete.presenterType == ImageType.Dreams) {
            await deleteCustomDream(avatarToDelete.id);
            await refetchPresentersList();
            return;
        }

        if (isExpressPresenter(avatarToDelete)) {
            await deleteAvatar(avatarToDelete.id);
            return;
        }

        if (isDvpPresenter(avatarToDelete)) {
            await deletePresenter((avatarToDelete as Presenter).id);
            return;
        }

        if (isDvpTrain(avatarToDelete)) {
            const trainId = (avatarToDelete as Presenter).train_id;

            if (trainId) {
                await deleteTrain(trainId);
            }
            return;
        }

        await deletePresenterImage((avatarToDelete as Presenter).id);
        await refetchPresentersList();
    };

    const isLoading = isDeletingPresenterImage || isDeletingCustomDream || isDeletingAvatar || isDeletingPresenter || isDeletingTrain;

    return (
        <DeleteModal
            itemType="image"
            mode="square"
            open={!!avatarToDelete}
            isLoading={isLoading}
            onDelete={handleDelete}
            onClose={() => setAvatarToDelete(null)}
            poster={avatarToDelete?.thumbnail ?? ''}
            text={{
                title: 'Delete Avatar?',
                content: 'Are you sure you want to delete this Avatar?',
                cta: 'Delete Avatar',
            }}
        />
    );
};
